import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import ComboGiftCard from "./ComboGiftCard";
import { Gift, Package, Heart } from "lucide-react";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
  </div>
);

// Error Component
const ErrorMessage = ({ message, onRetry }) => (
  <div className="text-center py-12">
    <div className="text-red-500 mb-4">
      Error: {message}
    </div>
    <button
      onClick={onRetry}
      className="bg-[#D4AF37] text-white px-4 py-2 rounded-sm hover:bg-[#C19B2E] transition-colors"
    >
      Try Again
    </button>
  </div>
);

// Empty State Component
const EmptyState = () => (
  <div className="text-center py-12">
    <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
    <h3 className="text-lg text-gray-500 mb-2">No Combo Packs Available</h3>
    <p className="text-sm text-gray-400">
      Check back soon for exciting combo offers!
    </p>
  </div>
);

const ComboAndGifts = () => {
  const [combos, setCombos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchCombos = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/combo/list/`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setCombos(data.results || []);
      setCurrentPage(data.page || 1);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error("Failed to fetch combo packs:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCombos();
  }, []);

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      fetchCombos(page);
      // Scroll to top of section
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 relative"
        >
          {/* Decorative Background */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-r from-transparent via-[#D4AF37]/10 to-transparent"></div>
          
          <div className="relative z-10">
            <h1
              className="text-xxl md:text-3xl bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-transparent bg-clip-text mb-4 tracking-wide"
              style={{ fontFamily: "serif" }}
            >
              <span className="text-[#D4AF37] mr-3">🎁</span>
              Combo Packs & Gifts
              <span className="text-[#D4AF37] ml-3">💝</span>
            </h1>
            
            <p className="text-md md:text-md text-gray-600 max-w-2xl mx-auto mb-6">
              Discover our carefully curated combo packs and gift sets, perfect for special occasions or treating yourself to luxury.
            </p>

            {/* Gold Accent Line */}
            <div className="mt-6 h-1 w-24 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] mx-auto rounded-full"></div>
          </div>
        </motion.div>

        {/* Content Section */}
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorMessage message={error} onRetry={() => fetchCombos(currentPage)} />
        ) : combos.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Results Info */}
            <div className="mb-6 text-center">
              <p className="text-sm text-gray-600">
                Showing {combos.length} combo pack{combos.length !== 1 ? 's' : ''} 
                {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
              </p>
            </div>

            {/* Combo Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {combos.map((combo, index) => (
                <ComboGiftCard 
                  key={combo._id} 
                  combo={combo} 
                  index={index}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex justify-center mt-8"
              >
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    &larr; Previous
                  </button>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                    <button
                      key={number}
                      onClick={() => handlePageChange(number)}
                      className={`px-4 py-2 rounded-sm border transition-all duration-200 ${
                        currentPage === number 
                          ? 'bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-white border-[#D4AF37] shadow-md' 
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-[#D4AF37]'
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Next &rarr;
                  </button>
                </nav>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ComboAndGifts;